/**
 * URL处理工具函数（与 uni-user-app 头像逻辑一致）
 */
import { API_HOST, API_BASE_URL, getAdStreamUrl, getVideoStreamUrl, getPlantingStreamUrl, isAdVideoId, isPlantingVideoId } from '@/config'

// 新用户注册 / 已登录但未上传头像时使用的默认头像
const DEFAULT_NEW_USER_AVATAR = new URL('../assets/images/M-09user.png', import.meta.url).href

// 去掉末尾斜杠，避免与 path 拼接时出现双斜杠
const HOST = (API_HOST || '').replace(/\/$/, '')

export function getFullUrl(url) {
  if (!url) return ''
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  const sep = url.startsWith('/') ? '' : '/'
  return `${HOST}${sep}${url}`
}

/**
 * 详情/列表项统一解析播放地址（剧场 OVideo、首页悬停预览等）。
 * - 广告：存在 ad_id 或 ID 形如 AD… 时，固定走后端 GET /api/v1/ad/{id}/stream（忽略接口里可能误写的 /ads/…）。
 * - 短视频：优先 stream_url / video_url（绝对或相对）；若为 /uploads/videos/ 等静态文件则忽略，走 GET /videos/{id}/stream?token=
 */
export function resolvePlaybackStreamUrl(v, token = null) {
  if (!v || typeof v !== 'object') return ''
  const id = v.video_id ?? v.ad_id ?? v.adId ?? v.planting_id ?? v.plantingId
  const isAd = v.ad_id != null || v.adId != null || isAdVideoId(id)
  if (isAd) {
    const adId = String(v.ad_id ?? v.adId ?? id ?? '').trim()
    if (adId) return getAdStreamUrl(adId, token)
    return ''
  }
  const isPlanting = v.planting_id != null || v.plantingId != null || isPlantingVideoId(id)
  if (isPlanting) {
    const pid = String(v.planting_id ?? v.plantingId ?? id ?? '').trim()
    if (pid) return getPlantingStreamUrl(pid, token)
    return ''
  }
  const direct = v.stream_url ?? v.video_url ?? v.videoUrl
  if (direct) {
    const s = String(direct).trim()
    const looksLikeUploadsFile =
      /\/uploads\/videos\//i.test(s) ||
      (/\/uploads\//i.test(s) && /\.mp4(\?|$)/i.test(s))
    if (!looksLikeUploadsFile) {
      if (s.startsWith('http://') || s.startsWith('https://')) return s
      if (s.startsWith('/')) return getFullUrl(s)
    }
  }
  return getVideoStreamUrl(id, token)
}

/** 评论抽屉、点赞等使用的统一 id（短视频为 video_id，广告为 ad_id） */
export function canonicalVideoId(v) {
  if (!v || typeof v !== 'object') return ''
  const x = v.video_id ?? v.ad_id ?? v.adId ?? v.planting_id ?? v.plantingId
  return x != null ? String(x) : ''
}

/**
 * 用户头像完整 URL。
 * - 有 userId 且 avatarPath 存在：优先走预览接口 GET /api/v1/users/{userId}/avatar?t=xxx（上传后通过该接口预览最新头像）。
 * - 有 userId 且 avatarPath 为空：视为首次登录/未设置头像，使用默认头像 M-09user。
 * - 无 userId：按 avatarPath（/uploads/、/api/v1/、http、相对路径）拼接。
 */
export function getFullAvatarUrl(userId, avatarPath) {
  const t = Date.now()
  // 已设置头像：走预览接口，确保总是拿到最新头像
  if (userId && avatarPath) {
    return `${API_BASE_URL}/users/${userId}/avatar?t=${t}`
  }
  // 首次登录/注册且未设置头像：使用默认头像 M-09user
  if (userId && !avatarPath) {
    return DEFAULT_NEW_USER_AVATAR
  }
  if (!avatarPath) return ''
  if (avatarPath.startsWith('/uploads/')) {
    return `${HOST}${avatarPath}${avatarPath.includes('?') ? '&' : '?'}t=${t}`
  }
  if (avatarPath.startsWith('/api/v1/users/')) {
    return `${HOST}${avatarPath}${avatarPath.includes('?') ? '&' : '?'}t=${t}`
  }
  if (avatarPath.startsWith('http')) {
    return avatarPath + (avatarPath.includes('?') ? '&' : '?') + `t=${t}`
  }
  const sep = avatarPath.startsWith('/') ? '' : '/'
  return `${HOST}${sep}${avatarPath}${avatarPath.includes('?') ? '&' : '?'}t=${t}`
}

export function getVideoCoverUrl(videoId) {
  if (!videoId) return ''
  return `${HOST}/api/v1/videos/${videoId}/cover`
}

/** 广告片封面：与短视频一致走 API，避免直接使用 /uploads/ 静态路径（服务端可能未挂载） */
export function getAdCoverUrl(adId) {
  if (!adId) return ''
  return `${HOST}/api/v1/ads/${adId}/cover`
}

export function getSeriesCoverUrl(seriesId) {
  if (!seriesId) return ''
  return `${HOST}/api/v1/series/${seriesId}/cover`
}

export function getUserAvatarUrl(userId) {
  if (!userId) return ''
  return `${API_BASE_URL}/users/${userId}/avatar`
}
